import {SkipIcon, DotFillIcon, QuestionBubbleIcon, ClockIcon, CircleSlashIcon} from '@primer/octicons-react'
import type {ComponentType} from 'react'
import {WorkingAsciiLoader} from '@github-ui/agent-sessions-interface/components/WorkingAsciiLoader'
import {isCopilotCodingAgent} from '@github-ui/copilot-shared-ui/agents/agent-helpers'
import {getReadableSessionState, SessionState, type Session} from '../types/session'
import styles from './StatusIcon.module.css'

export function SessionLoadingIcon({
  ariaLabel,
  'aria-label': accessibleLabel,
}: {
  ariaLabel?: string
  'aria-label'?: string
}) {
  return <WorkingAsciiLoader ariaLabel={ariaLabel ?? accessibleLabel} className={styles.loadingStatus} />
}

export function WorkingStatusIcon() {
  return <SessionLoadingIcon ariaLabel="Working" />
}

function NeutralSessionIcon({'aria-label': ariaLabel}: {'aria-label'?: string}) {
  return <DotFillIcon className={styles.neutralDot} aria-label={ariaLabel} />
}

export interface SessionStatusIconAttributes {
  icon: ComponentType<{'aria-label'?: string; className?: string}>
  color: string
  className?: string
  label: string
  'data-testid': string
}

export function getSessionStatusIconAttributes(
  status: string | undefined,
  agentSlug?: Session['agent_slug'],
): SessionStatusIconAttributes {
  switch (status) {
    case SessionState.InProgress:
      return {
        icon: SessionLoadingIcon,
        color: 'var(--fgColor-muted)',
        label: 'Working',
        'data-testid': 'icon-falling-sands',
      }
    case SessionState.Failed:
    case SessionState.TimedOut:
      return {
        icon: SkipIcon,
        color: 'var(--fgColor-danger)',
        className: 'fgColor-danger',
        label: 'Failed',
        'data-testid': 'icon-skip',
      }
    case SessionState.WaitingForUser:
      return {
        icon: QuestionBubbleIcon,
        color: 'var(--fgColor-attention)',
        className: 'fgColor-attention',
        label: 'Needs attention',
        'data-testid': 'icon-question-bubble',
      }
    case SessionState.Cancelled:
      if (isCopilotCodingAgent(agentSlug)) {
        return {
          icon: CircleSlashIcon,
          color: 'var(--fgColor-muted)',
          className: 'fgColor-muted',
          label: 'Cancelled',
          'data-testid': 'icon-circle-slash',
        }
      }
      break
    case SessionState.Queued:
      return {
        icon: ClockIcon,
        color: 'var(--fgColor-attention)',
        className: 'fgColor-attention',
        label: 'Queued',
        'data-testid': 'icon-clock',
      }
  }

  return {
    icon: NeutralSessionIcon,
    color: 'var(--fgColor-muted)',
    label: getReadableSessionState(status),
    'data-testid': 'icon-idle',
  }
}

export function getSessionStatusIcon(
  status: string | undefined,
  agentSlug?: Session['agent_slug'],
  statusLabel?: string,
) {
  const {icon: Icon, className, label} = getSessionStatusIconAttributes(status, agentSlug)
  const hasSeparateLabel = statusLabel && (status === SessionState.InProgress || status === SessionState.Queued)
  return <Icon className={className} aria-label={hasSeparateLabel ? undefined : label} />
}

export function InProgressSpinner() {
  return (
    <svg
      role="img"
      aria-label="Currently running"
      width="16px"
      height="16px"
      fill="none"
      viewBox="0 0 16 16"
      className="anim-rotate"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill="none"
        stroke="var(--fgColor-attention)"
        strokeWidth="2"
        d="M3.05 3.05a7 7 0 1 1 9.9 9.9 7 7 0 0 1-9.9-9.9Z"
        opacity=".5"
      />
      <path
        fill="var(--fgColor-attention)"
        fillRule="evenodd"
        d="M8 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8Z"
        clipRule="evenodd"
      />
      <path fill="var(--fgColor-attention)" d="M14 8a6 6 0 0 0-6-6V0a8 8 0 0 1 8 8h-2Z" />
    </svg>
  )
}
